import React, {FC, useCallback, useEffect, useState} from 'react'
import {useHistory} from 'react-router-dom'
import {ToastContainer} from 'react-toastify'
import {PageTitle} from '../../../../_metronic/layout/core'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import DataTable, {TableAction, TableColumn} from '../../../../shared/components/DataTable'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {apiErrorMessage} from '../../public/PublicModels'
import {fullName} from '../application/ApplicationModel'
import {candidateDetailPath} from '../candidate/CandidateModel'
import {formatDateTime} from '../shared/DocumentApi'
import {emptyFilters, IListFilters, SearchFilterBar} from '../shared/SearchFilterBar'
import {useJobProcessOptions} from '../shared/useJobProcessOptions'
import {TagChips} from '../tag/TagChips'
import {useTagOptions} from '../tag/useTagOptions'
import {removeFromCallPool, searchCallPool} from './Api'
import {CALL_ENTRY_PARAM, ICallPoolEntryModel} from './CallPoolModel'

export const callEntryDetailPath = (entry: ICallPoolEntryModel) =>
  `${candidateDetailPath(entry.candidateId)}?${CALL_ENTRY_PARAM}=${encodeURIComponent(entry.id)}`

/** Arama havuzu: oturumdaki kullanıcıya atanmış, aranmayı bekleyen adaylar */
const CallPoolPage: FC = () => {
  const history = useHistory()
  const {jobs} = useJobProcessOptions()
  const {tags} = useTagOptions()
  const [filters, setFilters] = useState<IListFilters>(emptyFilters)
  const [entries, setEntries] = useState<ICallPoolEntryModel[]>([])
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [totalElements, setTotalElements] = useState(0)
  const [loading, setLoading] = useState(false)
  const [removingItem, setRemovingItem] = useState<ICallPoolEntryModel | null>(null)

  const fetchEntries = useCallback(async (page: number, activeFilters: IListFilters) => {
    setLoading(true)
    try {
      const payload = (await searchCallPool(activeFilters, page)).data.payload
      setEntries(payload.content)
      setTotalPages(payload.totalPages)
      setTotalElements(payload.totalElements)
      setCurrentPage(page)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Arama listesi yüklenemedi'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchEntries(0, filters)
  }, [filters, fetchEntries])

  const handleSearch = useCallback((q: string) => setFilters((current) => ({...current, q})), [])
  const handleFilter = useCallback(
    (selection: Omit<IListFilters, 'q'>) => setFilters((current) => ({...current, ...selection})),
    []
  )

  const handleRemove = async () => {
    if (!removingItem) return
    try {
      await removeFromCallPool(removingItem.id)
      showSuccessToast('Aday arama listesinden çıkarıldı')
      await fetchEntries(currentPage, filters)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Çıkarılamadı'))
    } finally {
      setRemovingItem(null)
    }
  }

  const columns: TableColumn<ICallPoolEntryModel>[] = [
    {
      key: 'firstname',
      label: 'Aday',
      render: (_, entry) => (
        <div className='d-flex flex-column'>
          <span className='fw-bolder text-gray-800'>{fullName(entry)}</span>
          {entry.candidateLocation && <span className='text-muted fs-7'>{entry.candidateLocation}</span>}
          <TagChips tags={entry.tags} />
        </div>
      ),
    },
    {
      key: 'phone',
      label: 'İletişim',
      render: (_, entry) => (
        <div className='d-flex flex-column fs-7'>
          {entry.phone ? (
            <a href={`tel:${entry.phone.replace(/\s/g, '')}`} className='fw-bold text-primary'>
              <i className='bi bi-telephone me-1 text-primary'></i>
              {entry.phone}
            </a>
          ) : (
            <span>-</span>
          )}
          <span className='text-muted'>{entry.email}</span>
        </div>
      ),
    },
    {
      key: 'jobTitle',
      label: 'Kaynak',
      render: (_, entry) =>
        entry.jobTitle ? (
          <div className='d-flex flex-column fs-7'>
            <span className='badge badge-light-info align-self-start'>Başvuru</span>
            <span className='text-gray-700 mt-1'>{entry.jobTitle}</span>
          </div>
        ) : (
          <span className='badge badge-light-primary'>Aday havuzu</span>
        ),
    },
    {
      key: 'createdDate',
      label: 'Atanma',
      width: '160px',
      render: (_, entry) => (
        <div className='d-flex flex-column fs-7'>
          <span>{formatDateTime(entry.createdDate)}</span>
          {entry.assignedBy && <span className='text-muted'>{entry.assignedBy} tarafından</span>}
        </div>
      ),
    },
  ]

  const actions: TableAction<ICallPoolEntryModel>[] = [
    {
      label: 'Künye / Ara',
      icon: 'bi bi-telephone-outbound',
      className: 'btn-light-success me-2',
      onClick: (entry) => history.push(callEntryDetailPath(entry)),
    },
    {label: 'Listeden Çıkar', icon: 'bi bi-x-lg', className: 'btn-light-danger', onClick: setRemovingItem},
  ]

  return (
    <>
      <PageTitle breadcrumbs={[]}>Arama Havuzu</PageTitle>

      <div className='card'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>Arama Listem</span>
            <span className='text-muted mt-1 fw-bold fs-7'>
              Sana atanan {totalElements} aday aranmayı bekliyor. Künyeden Olumlu / Olumsuz sonuç girilen aday listeden düşer.
            </span>
          </h3>
        </div>
        <div className='card-body pt-0'>
          <SearchFilterBar jobs={jobs} tags={tags} onSearch={handleSearch} onFilter={handleFilter} />
          <DataTable
            data={entries}
            columns={columns}
            actions={actions}
            showPagination={true}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => page >= 0 && page < totalPages && fetchEntries(page, filters)}
            loading={loading}
            emptyMessage='Arama listende aday yok. Aday Havuzu veya Gelen Başvurular ekranından aktarabilirsin.'
          />
        </div>
      </div>

      <ConfirmModal
        show={!!removingItem}
        header='Listeden Çıkar'
        text={`${removingItem ? fullName(removingItem) : ''} aranmadan arama listenden çıkarılacak. Arama kaydı oluşturulmaz. Devam edilsin mi?`}
        handleClose={() => setRemovingItem(null)}
        applyOnClick={handleRemove}
      />

      <ToastContainer position='top-right' autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
    </>
  )
}

export {CallPoolPage}
